import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { Box, Checkbox, Chip, LinearProgress } from "@material-ui/core";
import * as _ from 'lodash';
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import { Link, useHistory } from "react-router-dom";
import moment from "moment-timezone";

import AppTable from "../Table/AppTable";
import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import AppUtil from "../../../../utilities/AppUtil";
import AppColor from "../../../../utilities/AppColor";
import InputSearch from "../../../../components/Input/InputSearch";
import AppConfig from "../../../../config/AppConfig";
import {
    fetchWorkerListPaging,
    getWorkerList,
    getWorkerListPaging,
    setWorkerListPagingQueries,
    resendOnboarding,
} from "../../../../store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import AppSelect from "components/Select/AppSelect";
import TablePaginationActions from "../Table/TablePaginationActions";

const useStyles = makeStyles((theme) => ({
    ...styles,
    container: {
        border: 'none',
        boxShadow: 'none',
    },
    divider: {
        marginTop: "1rem",
        marginBottom: "1rem",
    },
    w100: {
        width: '100%'
    },
    listContainer: {

    },
    list: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    listItem: {
        marginBottom: '20px',
        padding: '10px',
        border: '1px solid rgba(0, 0, 0, 0.2)',
        borderRadius: '5px',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        '&:hover': {
            backgroundColor: AppColor.mainColor,
            color: AppColor.white
        }
    },
    listTitle: {
        fontSize: '16px',
        color: AppColor.black,
        fontWeight: '500',
        padding: 0,
        margin: 0
    },
    progressing: {
        '&.progress': {
            backgroundColor: '#ababab'
        }
    },
    customTableFooter: {
        flexShrink: 0,
        marginLeft: "auto",
        marginRight: 0,
        paddingRight: 0,
        borderBottom: 'none',
        '& .MuiTablePagination-spacer': {
            flex: 0
        },
        '& .MuiToolbar-root': {
            justifyContent: 'flex-end',
            paddingRight: '0 !important'
        },
        "& .MuiTablePagination-root": {
            overflowX: 'visible'
        }
    },
    tagBase: {
        height: 24,
        fontSize: 11,
        borderRadius: 5,
        border: "none",
        "& .MuiChip-label": {
            paddingLeft: 10,
            paddingRight: 10,
        }
    },
    tagTraining: {
        margin: '2px',
    },
    tagSuccess: {
        backgroundColor: "#CCFBF1",
        color: "#0D9488",
    },
    tagWarning: {
        backgroundColor: "#FEF3C7",
        color: "#92400E",
    },
    tagDanger: {
        backgroundColor: "#FFE4E6",
        color: "#BE123C",
    },
    tagNeutral: {
        backgroundColor: "#F3F4F6",
        color: "#000000",
    },
    tagPending: {
        backgroundColor: "#FFEDD5",
        color: "#9A3412",
    },
    showMoreText: {
        cursor: 'pointer',
        color: '#0D9488',
        fontSize: 11,
        marginLeft: 4,
        textDecoration: 'underline'
    },
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap'
    },
    searchWrapper: {
        flex: 1,
        minWidth: 200,
    },
}));

const headCells = [
    {
        id: "name",
        numeric: "left",
        disablePadding: false,
        label: "label.worker_name",
        sort: true,
    },
    {
        id: "workerType",
        numeric: "left",
        disablePadding: false,
        label: "label.role",
        sort: true,
    },
    {
        id: "house",
        numeric: "left",
        disablePadding: false,
        label: "customUI.house",
    },
    {
        id: "ndisScreening",
        numeric: "left",
        disablePadding: false,
        label: "customUI.ndisScreening",
    },
    {
        id: "training",
        numeric: "left",
        disablePadding: false,
        label: "customUI.training",
    },
    {
        id: "appraisal",
        numeric: "left",
        label: "customUI.appraisal",
    },
    {
        id: "status",
        numeric: "left",
        label: "label.status",
        sort: true,
    },
    {
        id: "actions",
        numeric: "left",
        label: "label.actions",
        sort: false,
    },
];

const TableInformation = forwardRef(({ selected, setSelected }, ref) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const isMobile = false;
    const dispatch = useDispatch();
    const savedListPaging = useSelector(getWorkerListPaging);
    const statusOptions = [
        { value: 'ACTIVE', label: t('system_status.ACTIVE'), statuses: ['ACTIVE'], approvalStatus: 'ACTIVE' },
        { value: 'PENDING', label: t('approval_status.PENDING'), statuses: undefined, approvalStatus: 'PENDING' },
        { value: 'REJECT', label: t('approval_status.REJECT'), statuses: undefined, approvalStatus: 'REJECT' },
        { value: 'WAITING_FOR_APPROVAL', label: t('approval_status.WAITING_FOR_APPROVAL'), statuses: undefined, approvalStatus: 'WAITING_FOR_APPROVAL' },
        { value: 'DEACTIVATE', label: t('system_status.DEACTIVATE'), statuses: ['DEACTIVATE'], approvalStatus: 'ACTIVE' },
        { value: 'TERMINATE', label: t('system_status.TERMINATE'), statuses: ['TERMINATE'], approvalStatus: 'ACTIVE' },
    ];
    const [queries, setQueries] = useState({
        asc_sort: savedListPaging.queries.asc_sort,
        page: savedListPaging.queries.page,
        size: savedListPaging.queries.size || (isMobile ? 25 : 10),
        search_key: savedListPaging.queries.search_key,
        sort_field: savedListPaging.queries.sort_field,
        statuses: savedListPaging.queries.statuses,
        approvalStatus: savedListPaging.queries.approvalStatus,
    });
    const [selectedStatusOption, setSelectedStatusOption] = useState(
        statusOptions.find(opt => {
          const statuses = opt.statuses?.[0];
          const savedStatuses = savedListPaging.queries.statuses?.[0];
          return statuses === savedStatuses && opt.approvalStatus === savedListPaging.queries.approvalStatus
        }) || null
    );
    
    const workerList = useSelector(getWorkerList);
    const authInfo = useSelector(getAuthInfo);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totalElements, setTotalElements] = useState(savedListPaging.totalElements || 0);
    const [searchKey, setSearchKey] = useState(savedListPaging.queries.search_key || "");
    const [expandedTraining, setExpandedTraining] = useState({});

    const emptyRows = data.length === 0;

    useImperativeHandle(ref, () => ({
        refreshData: () => _loadData(queries),
        loadWithQueries: (q) => _loadData(q),
        getCurrentQueries: () => queries,
        getDataLength: () => data.length,
    }));

    const _handleSelect = (id) => {
        if (!selected.includes(id)) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter(item => item !== id))
        }
    }

    const _handleSelectAll = () => {
        if (data.length === countSelectInPage()) {
            setSelected(selected.filter(item => !data.find(d => d.id === item)));
        } else {
            setSelected(_.union(selected, data.map(item => item.id)));
        }
    }

    const handleChangePage = (event, newPage) => {
        _loadData({ ...queries, page: newPage + 1 });
        AppUtil.scrollToTopById("main-panel");
    };

    const handleChangeRowsPerPage = (event) => {
        _loadData({
            ...queries,
            page: 1,
            size: parseInt(event.target.value),
        });
        AppUtil.scrollToTopById("main-panel");
    };

    const onRequestSort = (event, property) => {
        _loadData({
            ...queries,
            sort_field: property,
            asc_sort:
                property === queries.sort_field ? queries.asc_sort !== true : true,
        });
    };

    const onSearch = (value) => {
        setSearchKey(value);
        _loadData({
            ...queries,
            search_key: value,
            page: 1,
        });
    };

    const _loadData = async ({ page, size, search_key, asc_sort, sort_field, statuses, approvalStatus } = {}) => {
        const newQueries = {
            ...queries,
            search_key: searchKey
        };
        if (page) {
            newQueries.page = page;
        }
        if (size) {
            newQueries.size = size;
        }
        if (sort_field) {
            newQueries.sort_field = sort_field;
        }
        if (statuses !== undefined) newQueries.statuses = statuses;
        if (approvalStatus !== undefined) newQueries.approvalStatus = approvalStatus;
        if (search_key || search_key === '') {
            newQueries.search_key = search_key;
        }

        newQueries.asc_sort = !(!asc_sort || false);
        newQueries.companyId = authInfo.companyId;
        setLoading(true);
        const result = await dispatch(fetchWorkerListPaging(newQueries));
        const { payload } = result;

        if (payload) {
            setQueries({ ...newQueries });
            setTotalElements(payload.totalElements);
            setLoading(false);
            
            dispatch(setWorkerListPagingQueries({
                page: newQueries.page,
                size: newQueries.size,
                search_key: newQueries.search_key,
                sort_field: newQueries.sort_field,
                asc_sort: newQueries.asc_sort,
                statuses: statuses,
                approvalStatus: approvalStatus ? approvalStatus : null,
            }));
        } else if (newQueries.page > 1) {
            _loadData({ ...newQueries, page: newQueries.page - 1 });
        } else {
            setQueries({ ...newQueries });
            setTotalElements(0);
            setData([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        _loadData(queries);
    }, []);

    useEffect(() => {
        if (workerList)
            setData(workerList);
    }, [workerList]);

    const countSelectInPage = () => {
        const list = data.filter(item => {
            for (const i in selected) {
                if (item.id === selected[i]) {
                    return true;
                }
            }
            return false;
        });
        return list.length;
    };

    const handleReSendEmailUpdateInfo = (id) => {
        if (id || id === 0) {
            console.log('window.location: ', window.location);
            dispatch(resendOnboarding({
                domain: window.location.hostname,
                workerId: id
            }))
        }

    };

    const renderNdisScreening = (row) => {
        if (!row.ndisExpireDate) {
            return '-';
        };
        const expireDate = moment(row.ndisExpireDate);
        const diffDays = expireDate.diff(moment(), 'days');

        if (diffDays < 0) {
            return <Chip size="small" className={`${classes.tagBase} ${classes.tagDanger}`} label={t('customUI.overdue')} />;
        } else if (diffDays <= 90) {
            return <Chip size="small" className={`${classes.tagBase} ${classes.tagDanger}`} label={`Exp ${expireDate.format('DD MMM')}`} />;
        } else {
            return <Chip size="small" className={`${classes.tagBase} ${classes.tagSuccess}`} label={`Valid · ${expireDate.format('YYYY')}`} />;
        }
    };

    const renderAppraisal = (row) => {
        if (!row.appraisalDueDate) return '-';
        const expireDate = moment(row.appraisalDueDate);
        const diffDays = expireDate.diff(moment(), 'days');

        if (diffDays < 0) {
            return <Chip size="small" className={`${classes.tagBase} ${classes.tagDanger}`} label={t('customUI.overdue')} />;
        } else if (diffDays <= 90) {
            return <Chip size="small" className={`${classes.tagBase} ${classes.tagDanger}`} label={`Exp ${expireDate.format('DD MMM YYYY')}`} />;
        } else {
            const label = row.isProbation ? `Probation ${expireDate.format('MMM')}` : expireDate.format('MMM YYYY');
            const colorClass = row.isProbation ? classes.tagNeutral : classes.tagSuccess;
            return <Chip size="small" className={`${classes.tagBase} ${colorClass}`} label={label} />;
        }
    };

    const renderTraining = (row, index) => {
        const fields = [
            "workerHandHygiene",
            "workerMedicationManagement",
            "workerInfectionControl",
            "workerSafeFoodHandling",
            "workerNdisOrientationModule",
            "workerSupportingEffectiveCommunication",
            "workerNdisSupportingSafeAndEnjoyableMeals",
            "workerSupportingPeopleToStayInfectionFree",
        ];

        const issues = [];

        for (const field of fields) {
            const data = row[field];
            let text = null;
            let type = null;

            if (!data || !data.missing) {
                type = 'danger';
                text = `${t(`trainingTag.${field}`)}`;
            }

            if (data && data.expire) {
                const diffDays = moment(data.expire).diff(moment(), 'days');
                if (diffDays < 0) {
                    type = 'danger';
                    text = `${t(`trainingTag.${field}`)} ${t('label.overdue')}`;
                } else if (diffDays <= 90) {
                    type = 'warning';
                    text = `${t(`trainingTag.${field}`)} Exp ${moment(data.expire).format('DD MMM YYYY')}`;
                }
            }

            if (text && type) {
                issues.push({ field, type, text });
            }

        }

        if (issues.length === 0) {
            return <Chip size="small" className={`${classes.tagBase} ${classes.tagSuccess}`} label={t('customUI.allComplete')} />;
        }

        const isExpanded = expandedTraining[row.id];
        const visibleIssues = isExpanded ? issues : issues.slice(0, 2);

        return (
            <Box display="flex" flexDirection="column" alignItems="flex-start">
                <Box display="flex" flexDirection="column" style={{ gap: 2 }}>
                    {visibleIssues.map((issue, idx) => (
                        <Chip
                            key={idx}
                            size="small"
                            className={`${classes.tagBase} ${classes.tagTraining} ${issue.type === 'danger' ? classes.tagDanger : classes.tagWarning}`}
                            label={issue.text}
                        />
                    ))}
                </Box>
                {!isExpanded && issues.length > 2 && (
                    <span className={classes.showMoreText} onClick={() => setExpandedTraining({ ...expandedTraining, [row.id]: true })}>
                        + {issues.length - 2} {t('button.showMore')}
                    </span>
                )}
                {isExpanded && (
                    <span className={classes.showMoreText} onClick={() => setExpandedTraining({ ...expandedTraining, [row.id]: false })}>
                        {t('button.showLess')}
                    </span>
                )}
            </Box>
        );
    };

    return (
        <div>
            <Box className={classes.filterRow} mb={2}>
                <Box className={classes.searchWrapper}>
                    <InputSearch
                        onSearch={onSearch}
                        searchText={searchKey}
                        containerStyle={{ height: '41px' }}
                    />
                </Box>
                <Box style={{ width: 200 }}>
                    <AppSelect
                        name="statusFilter"
                        placeholder={t('label.status')}
                        isNotMargin={true}
                        value={selectedStatusOption}
                        options={statusOptions}
                        onChange={option => {
                            setSelectedStatusOption(option);
                            _loadData({
                                statuses: option ? option.statuses : null,
                                approvalStatus: option ? option.approvalStatus : null,
                                page: 1
                            });
                        }}
                        allowOverflow={true}
                    />
                </Box>
            </Box>

            {!isMobile && <AppTable
                onRequestSort={onRequestSort}
                order={queries.asc_sort === true ? "asc" : "desc"}
                orderBy={queries.sort_field}
                rowCount={data.length}
                headCells={headCells}
                numSelected={countSelectInPage()}
                onSelectAllClick={_handleSelectAll}
                classesContainer={classes.container}
            >
                <TableBody>
                    {loading && (<TableRow>
                        <TableCell colSpan={8} padding="none">
                            <LinearProgress />
                        </TableCell>
                    </TableRow>)}
                    {data.map((row, index) => {
                        let status = row.approvalStatus;
                        if (row.approvalStatus === AppConfig.APPROVAL_STATUS.ACTIVE) {
                            status = row.status;
                        }
                        return (
                            <TableRow key={index}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected.indexOf(row.id) !== -1}
                                        inputProps={{ 'aria-labelledby': row.id }}
                                        onClick={() => _handleSelect(row.id)}
                                        color="primary"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Link to={`/admin/workers/${row.id}`} style={{ textDecoration: 'none' }}
                                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                                    >
                                        {row.name}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    {row.role && t(`worker_type.${row.role}`)}
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell>{renderNdisScreening(row)}</TableCell>
                                <TableCell>{renderTraining(row, index)}</TableCell>
                                <TableCell>{renderAppraisal(row)}</TableCell>
                                <TableCell>
                                    <Chip
                                        className={classes.tagBase}
                                        style={{
                                            backgroundColor: AppConfig.SYSTEM_STATUS.ACTIVE === status
                                                ? "#CCFBF1"
                                                : AppConfig.SYSTEM_STATUS.DEACTIVATE === status || AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL === status
                                                    ? AppColor.orange
                                                    : AppColor.red,
                                            color: AppConfig.SYSTEM_STATUS.ACTIVE === status ? "#0D9488" : AppColor.white,
                                            fontWeight: 'bold'
                                        }}
                                        size="small"
                                        label={status === AppConfig.APPROVAL_STATUS.REJECT ? t(`approval_status.${status}`) : t(`system_status.${status}`)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }} className="text-center">
                                        <Link to={`/admin/workers/${row.id}`}>
                                            <i className="fas fa-edit" title={t("button.edit")} style={{ marginRight: '10px' }} />
                                        </Link>
                                        {
                                          (row?.approvalStatus === AppConfig.APPROVAL_STATUS.PENDING || row?.approvalStatus === AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL) &&
                                          <div style={{ cursor: 'pointer' }}>
                                              <i className="fas fa-paper-plane" title={t("label.resendEmail")} onClick={() => handleReSendEmailUpdateInfo(row.id)} />
                                          </div>
                                        }
                                    </div>
                                </TableCell>
                            </TableRow>
                        )
                    })
                    }

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 }}>
                            <TableCell colSpan={headCells.length} className="text-center">
                                {t("label.no_data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            colSpan={headCells.length + 1}
                            count={totalElements}
                            rowsPerPage={queries.size}
                            page={queries.page - 1}
                            SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelRowsPerPage={`${t("label.rows_per_page")}:`}
                            labelDisplayedRows={({ from, to, count }) =>
                                t("label.from_to", {
                                    from,
                                    to,
                                    count:
                                        count !== -1 ? count : `${t("text.more_than")} ${to}`,
                                })
                            }
                            style={{
                                overflowX: 'hidden'
                            }}
                            className={classes.customTableFooter}
                        />
                    </TableRow>
                </TableFooter>
            </AppTable>}
        </div>
    );
});

export default TableInformation;
